/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { APPLICATION_VIEW_MASTER, APPLICATION_VIEW_OFFICE } from '@gh/builder-shared-data';

import { createRoute } from '@gh/common-shared';
import {
  SystemVarsAccountingExpenseSubtabComponent,
  SystemVarsAccountingJobsSubtabComponent,
  SystemVarsAccountingReceivablesSubtabComponent,
  SystemVarsAccountingTabComponent,
} from './components/system-vars-accounting-tab';
import { SystemVarsAccountsIntranetSubtabComponent } from './components/system-vars-accounts-tab/system-vars-accounts-intranet-subtab/system-vars-accounts-intranet-subtab.component';
import { SystemVarsAccountsTwilioTabComponent } from './components/system-vars-accounts-tab/system-vars-accounts-twilio-subtab/system-vars-accounts-twilio-subtab.component';

import { SystemVarsAccountsTabComponent } from './components/system-vars-accounts-tab/system-vars-accounts-tab.component';
import { SystemVarsAccountsXeroTabComponent } from './components/system-vars-accounts-tab/system-vars-accounts-xero-subtab/system-vars-accounts-xero-subtab.component';
import { SystemVarsContactsTabComponent } from './components/system-vars-contacts-tab/system-vars-contacts-tab.component';
import { SystemVarsEstimatingTabComponent } from './components/system-vars-estimating-tab/system-vars-estimating-tab.component';
import { SystemVarsFranchiseeTabComponent } from './components/system-vars-franchisee-tab/system-vars-franchisee-tab.component';
import { SystemVarsJobsTabComponent } from './components/system-vars-jobs-tab/system-vars-jobs-tab.component';
import { SystemVarsLedgerTabComponent } from './components/system-vars-ledger-tab/system-vars-ledger-tab.component';
import { SystemVarsManagerTabComponent } from './components/system-vars-manager-tab/system-vars-manager-tab.component';
import { SystemVarsMasterZoneTabComponent } from './components/system-vars-master-zone-tab/system-vars-master-zone-tab.component';
import { SystemVarsPayablesTabComponent } from './components/system-vars-payables-tab/system-vars-payables-tab.component';
import { SystemVarsPrinterListTabComponent } from './components/system-vars-printer-list-tab/system-vars-printer-list-tab.component';
import { SystemVarsReceivablesTabComponent } from './components/system-vars-receivables-tab/system-vars-receivables-tab.component';
import { SystemVarsSalesTabComponent } from './components/system-vars-sales-tab/system-vars-sales-tab.component';
import { SystemVarsSupervisorTabComponent } from './components/system-vars-supervisor-tab/system-vars-supervisor-tab.component';
import { SystemVarsTabContainerComponent } from './components/system-vars-tab-container/system-vars-tab-container.component';
import { SystemVarsAccountingBusinessService, SystemVarsPrinterListTabBusinessService } from './resources';
import { SystemVarsAccountsTabBusinessService } from './resources/system-vars-accounts-tab-business.service';
import { SystemVarsContactsTabBusinessService } from './resources/system-vars-contacts-tab-business.service';
import { SystemVarsEstimatingBusinessService } from './resources/system-vars-estimating-business.service';
import { SystemVarsFranchiseeBusinessService } from './resources/system-vars-franchisee-business.service';
import { SystemVarsJobsBusinessService } from './resources/system-vars-jobs-business.service';
import { SystemVarsLedgerTabBusinessService } from './resources/system-vars-ledger-tab-business.service';
import { SystemVarsManagerTabBusinessService } from './resources/system-vars-manager-tab-business.service';
import { SystemVarsMasterZoneTabBusinessService } from './resources/system-vars-master-zone-tab-business.service';
import { SystemVarsPayablesTabBusinessService } from './resources/system-vars-payables-tab-business.service';
import { SystemVarsReceivablesTabBusinessService } from './resources/system-vars-receivables-tab-business.service';
import { SystemVarsSalesBusinessService } from './resources/system-vars-sales-business.service';
import { SystemVarsSupervisorTabBusinessService } from './resources/system-vars-supervisor-tab-business.service';

const routes = [
  createRoute({
    component: SystemVarsTabContainerComponent,
    path: '',
    children: [
      createRoute({
        component: SystemVarsFranchiseeTabComponent,
        path: 'franchisee',
        init: SystemVarsFranchiseeBusinessService,
        leave: true,
        auth: {
          applicationView: APPLICATION_VIEW_OFFICE,
          permission: ['Setup.SystemVariables.Franchisee.VIEW'],
        },
      }),
      createRoute({
        component: SystemVarsSalesTabComponent,
        path: 'sales',
        init: SystemVarsSalesBusinessService,
        leave: true,
        auth: {
          applicationView: APPLICATION_VIEW_OFFICE,
          permission: ['Setup.SystemVariables.Sales.VIEW'],
        },
      }),
      createRoute({
        component: SystemVarsAccountsTabComponent,
        path: 'accounts',
        children: [
          createRoute({
            component: SystemVarsAccountsTwilioTabComponent,
            path: 'twilio',
            init: {
              service: SystemVarsAccountsTabBusinessService,
              selector: 'initTwilioView',
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
              permission: [
                'Setup.SystemVariables.Accounts.VIEW',
                'Setup.SystemVariables.Accounts.EDIT',
              ],
            },
            leave: true,
          }),
          createRoute({
            component: SystemVarsAccountsXeroTabComponent,
            path: 'xero',
            init: {
              service: SystemVarsAccountsTabBusinessService,
              selector: 'initXeroView',
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
              permission: [
                'Setup.SystemVariables.Accounts.VIEW',
                'Setup.SystemVariables.Accounts.EDIT',
              ],
            },
          }),
          createRoute({
            component: SystemVarsAccountsIntranetSubtabComponent,
            path: 'intranet',
            init: {
              service: SystemVarsAccountsTabBusinessService,
              selector: 'initIntranetView',
            },
            leave: true,
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
              permission: [
                'Setup.SystemVariables.Accounts.VIEW',
                'Setup.SystemVariables.Accounts.EDIT',
                'Setup.SystemVariables.Accounts.INITIATE',
              ],
            },
          }),
        ],
      }),
    ],
  }),
  createRoute({
    component: SystemVarsTabContainerComponent,
    path: '',
    children: [
      createRoute({
        component: SystemVarsMasterZoneTabComponent,
        path: 'master-zone',
        init: SystemVarsMasterZoneTabBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_MASTER],
        },
      }),
      createRoute({
        component: SystemVarsContactsTabComponent,
        path: 'contacts',
        init: SystemVarsContactsTabBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Contacts.VIEW',
            'Setup.SystemVariables.Contacts.EDIT',
          ],
        },
        leave: true,
      }),
      createRoute({
        component: SystemVarsEstimatingTabComponent,
        path: 'estimating',
        init: SystemVarsEstimatingBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Estimating.VIEW',
            'Setup.SystemVariables.Estimating.EDIT',
          ],
        },
        leave: true,
      }),
      createRoute({
        component: SystemVarsJobsTabComponent,
        path: 'jobs',
        init: SystemVarsJobsBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Jobs.VIEW',
            'Setup.SystemVariables.Jobs.EDIT',
          ],
        },
        leave: true,
      }),
      createRoute({
        component: SystemVarsPrinterListTabComponent,
        path: 'printers',
        init: SystemVarsPrinterListTabBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Printers.VIEW',
            'Setup.SystemVariables.Printers.EDIT',
          ],
        },
      }),
      createRoute({
        component: SystemVarsReceivablesTabComponent,
        path: 'receivables',
        init: SystemVarsReceivablesTabBusinessService,
        leave: true,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Receivables.VIEW',
            'Setup.SystemVariables.Receivables.EDIT',
          ],
        },
      }),
      createRoute({
        component: SystemVarsManagerTabComponent,
        path: 'manager',
        init: SystemVarsManagerTabBusinessService,
        leave: true,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Manager.VIEW',
            'Setup.SystemVariables.Manager.EDIT',
          ],
        },
      }),
      createRoute({
        component: SystemVarsSupervisorTabComponent,
        path: 'supervisor',
        init: SystemVarsSupervisorTabBusinessService,
        leave: true,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Supervisor.VIEW',
            'Setup.SystemVariables.Supervisor.EDIT',
          ],
        },
      }),
      createRoute({
        component: SystemVarsPayablesTabComponent,
        path: 'payables',
        leave: true,
        init: SystemVarsPayablesTabBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Payables.VIEW',
            'Setup.SystemVariables.Payables.EDIT',
          ],
        },
      }),
      createRoute({
        component: SystemVarsLedgerTabComponent,
        path: 'ledger',
        leave: true,
        init: SystemVarsLedgerTabBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.SystemVariables.Ledger.VIEW',
            'Setup.SystemVariables.Ledger.EDIT',
          ],
        },
      }),
      createRoute({
        component: SystemVarsAccountingTabComponent,
        path: 'accounting',
        auth: {
          applicationView: APPLICATION_VIEW_OFFICE,
          permission: [
            'Setup.SystemVariables.Accounting.VIEW',
            'Setup.SystemVariables.Accounting.EDIT',
          ],
        },
        children: [
          createRoute({
            component: SystemVarsAccountingReceivablesSubtabComponent,
            path: 'receivables',
            init: {
              service: SystemVarsAccountingBusinessService,
              selector: 'initReceivables',
            },
            leave: true,
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
            },
          }),
          createRoute({
            component: SystemVarsAccountingExpenseSubtabComponent,
            path: 'expense',
            init: {
              service: SystemVarsAccountingBusinessService,
              selector: 'initExpense',
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
            },
            leave: true,
          }),
          createRoute({
            component: SystemVarsAccountingJobsSubtabComponent,
            path: 'overheads',
            init: {
              service: SystemVarsAccountingBusinessService,
              selector: 'initOverheads',
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
            },
            leave: true,
          }),
          createRoute({
            component: SystemVarsAccountingJobsSubtabComponent,
            path: 'advertise',
            init: {
              service: SystemVarsAccountingBusinessService,
              selector: 'initAdvertise',
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
            },
            leave: true,
          }),
          createRoute({
            component: SystemVarsAccountingJobsSubtabComponent,
            path: 'wages',
            init: {
              service: SystemVarsAccountingBusinessService,
              selector: 'initWages',
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
            },
            leave: true,
          }),
          createRoute({
            component: SystemVarsAccountingJobsSubtabComponent,
            path: 'liabilities',
            init: {
             service: SystemVarsAccountingBusinessService,
             selector: 'initLiabilities',
            },
            auth: {
              applicationView: APPLICATION_VIEW_OFFICE,
            },
            leave: true,
          }),
        ],
      }),
    ],
  }),
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class BuilderSetupSystemVarsRoutingModule {
}
